/**
 * Platform Admin — mock data fixtures (AK-216/217/218 Sprint 1).
 *
 * Mock-first pattern: страницы Platform Admin поставлялись на staging до того,
 * как backend AK-215 уедет в прод. Включается **только** явным
 * `VITE_PLATFORM_ADMIN_USE_MOCK=true` и остался ради разработки без бэкенда.
 *
 * Осталось здесь ровно то, что бэкенд ещё не отдаёт целиком — реестр
 * компаний. Фикстуры KPI дашборда (AK-470) убраны: живой эндпоинт
 * `/platform-admin/dashboard/kpis` есть, а сочинённые «47 tenants,
 * 1 248 000 ₽ выручки» в админке founder'а хуже честного прочерка.
 *
 * Источник цифр — wireframe `docs/superpowers/prototypes/2026-06-07-ak190-platform-admin/`
 * (founder approved 2026-06-21).
 */

export type TenantStatus = 'active' | 'trial' | 'paused' | 'archived' | 'suspended';
export type TenantTariff = 'free' | 'trial' | 'pro' | 'enterprise' | 'lifetime';

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  /**
   * `null` — платформа не знает тариф. Реальный `/platform/companies` его не
   * отдаёт (биллинг живёт в отдельном эндпоинте на компанию), поэтому в
   * живом режиме колонка показывает «—», а не выдуманное значение.
   */
  tariff: TenantTariff | null;
  users_count: number;
  /** `null` — площадь не посчитана (живой список её не отдаёт). */
  area_ha: number | null;
  /** ISO-8601 date «платно до». `null` — платформа не знает срок. */
  paid_until: string | null;
}

export interface CompaniesListQuery {
  search?: string;
  status?: TenantStatus | 'all';
  tariff?: TenantTariff | 'all';
  /** AK-348: offset-пагинация для бесконечного списка. */
  offset?: number;
  limit?: number;
}

export interface CompaniesListResponse {
  items: TenantSummary[];
  total: number;
  offset: number;
  limit: number;
}

// ---------- Fixtures ----------

const MOCK_TENANTS: TenantSummary[] = [
  { id: '01', name: 'Кубаньагро', slug: 'kubanagro', status: 'active', tariff: 'pro', users_count: 28, area_ha: 42_100, paid_until: '2026-12-31' },
  { id: '02', name: 'Дон-Зерно', slug: 'don-zerno', status: 'active', tariff: 'pro', users_count: 19, area_ha: 31_850, paid_until: '2026-09-15' },
  { id: '03', name: 'Северный колос', slug: 'severkolos', status: 'active', tariff: 'pro', users_count: 15, area_ha: 27_400, paid_until: '2026-10-30' },
  { id: '04', name: 'Тамбов-Поле', slug: 'tambovpole', status: 'active', tariff: 'pro', users_count: 12, area_ha: 22_100, paid_until: '2027-01-15' },
  { id: '05', name: 'Кристалл (демо)', slug: 'crystal', status: 'trial', tariff: 'trial', users_count: 8, area_ha: 18_500, paid_until: '2026-07-01' },
  { id: '06', name: 'ВолгаАгро', slug: 'volgaagro', status: 'active', tariff: 'pro', users_count: 14, area_ha: 17_200, paid_until: '2026-11-01' },
  { id: '07', name: 'СтавропольАгро', slug: 'stavagro', status: 'active', tariff: 'pro', users_count: 11, area_ha: 14_800, paid_until: '2026-08-22' },
  { id: '08', name: 'Алтай-Поле', slug: 'altaipole', status: 'paused', tariff: 'free', users_count: 6, area_ha: 12_300, paid_until: '2026-05-30' },
  { id: '09', name: 'Курск-Зерно', slug: 'kurskzerno', status: 'active', tariff: 'pro', users_count: 9, area_ha: 11_100, paid_until: '2026-12-01' },
  { id: '10', name: 'Орёл-Агро', slug: 'orelagro', status: 'active', tariff: 'pro', users_count: 7, area_ha: 9_800, paid_until: '2027-02-15' },
  { id: '11', name: 'Псков-Поле', slug: 'pskovpole', status: 'archived', tariff: 'free', users_count: 0, area_ha: 3_200, paid_until: null },
  { id: '12', name: 'Воронеж-Агро', slug: 'voronezhagro', status: 'active', tariff: 'enterprise', users_count: 34, area_ha: 8_500, paid_until: null },
  { id: '13', name: 'Сибирь-Зерно', slug: 'sibzerno', status: 'active', tariff: 'pro', users_count: 16, area_ha: 7_900, paid_until: '2026-10-12' },
  { id: '14', name: 'Урал-Агро', slug: 'uralagro', status: 'paused', tariff: 'pro', users_count: 4, area_ha: 6_400, paid_until: '2026-04-10' },
  { id: '15', name: 'Самара-Поле', slug: 'samarapole', status: 'active', tariff: 'pro', users_count: 10, area_ha: 5_800, paid_until: '2026-12-20' },
  { id: '16', name: 'Белгород-Зерно', slug: 'belzerno', status: 'trial', tariff: 'trial', users_count: 5, area_ha: 4_900, paid_until: '2026-07-15' },
  { id: '17', name: 'Липецк-Агро', slug: 'lipagro', status: 'active', tariff: 'pro', users_count: 8, area_ha: 4_300, paid_until: '2026-09-30' },
  { id: '18', name: 'Рязань-Поле', slug: 'ryazanpole', status: 'active', tariff: 'free', users_count: 3, area_ha: 3_900, paid_until: null },
  { id: '19', name: 'Тула-Зерно', slug: 'tulzerno', status: 'archived', tariff: 'free', users_count: 0, area_ha: 1_800, paid_until: null },
  { id: '20', name: 'Брянск-Агро', slug: 'bryagro', status: 'active', tariff: 'pro', users_count: 7, area_ha: 3_100, paid_until: '2026-11-25' },
  { id: '21', name: 'Калуга-Поле', slug: 'kalpole', status: 'active', tariff: 'lifetime', users_count: 12, area_ha: 2_600, paid_until: null },
];

// ---------- Fetchers (simulate latency) ----------

const MOCK_DELAY_MS = 250;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), MOCK_DELAY_MS));
}

/**
 * AK-348: подложка для бесконечного списка. Реальный фикстур — 21 tenant;
 * чтобы прочувствовать виртуализацию/автоподгрузку, детерминированно
 * размножаем набор до ~600 строк (стабильные id/slug на основе индекса).
 */
const MOCK_TENANTS_LARGE: TenantSummary[] = Array.from(
  { length: 600 },
  (_, i) => {
    const base = MOCK_TENANTS[i % MOCK_TENANTS.length];
    if (i < MOCK_TENANTS.length) return base;
    const gen = Math.floor(i / MOCK_TENANTS.length) + 1;
    return {
      ...base,
      id: `${base.id}-${gen}`,
      name: `${base.name} №${gen}`,
      slug: `${base.slug}-${gen}`,
    };
  },
);

export async function fetchCompaniesListMock(
  query: CompaniesListQuery,
): Promise<CompaniesListResponse> {
  const { search, status, tariff, offset = 0, limit = 100 } = query;

  let filtered = MOCK_TENANTS_LARGE;

  if (search && search.trim()) {
    const needle = search.trim().toLowerCase();
    filtered = filtered.filter(
      (t) => t.name.toLowerCase().includes(needle) || t.slug.toLowerCase().includes(needle),
    );
  }
  if (status && status !== 'all') {
    filtered = filtered.filter((t) => t.status === status);
  }
  if (tariff && tariff !== 'all') {
    filtered = filtered.filter((t) => t.tariff === tariff);
  }

  const total = filtered.length;
  const items = filtered.slice(offset, offset + limit);

  return delay({ items, total, offset, limit });
}
